import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import AccountPageScroll from "../myaccount/components/AccountPageScroll";

const REDIRECT_SECONDS = 8;

function PayhereCancel() {
  const navigate = useNavigate();
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);

  useEffect(() => {
    const redirectTimer = setTimeout(() => {
      navigate("/");
    }, REDIRECT_SECONDS * 1000);

    const countdown = setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => {
      clearTimeout(redirectTimer);
      clearInterval(countdown);
    };
  }, [navigate]);

  const progress = ((REDIRECT_SECONDS - secondsLeft) / REDIRECT_SECONDS) * 100;

  return (
    <AccountPageScroll className="bg-slate-50 dark:bg-slate-950">
      <div className="relative flex min-h-dvh items-center justify-center overflow-hidden px-4 py-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-[28%] h-80 w-[min(520px,90vw)] -translate-x-1/2 bg-[radial-gradient(ellipse_at_center,rgba(245,158,11,0.16),transparent_70%)] dark:bg-[radial-gradient(ellipse_at_center,rgba(245,158,11,0.12),transparent_70%)]"
        />

        <article className="relative z-10 w-full max-w-[440px] overflow-hidden rounded-[20px] border border-slate-200 bg-white px-7 pb-6 pt-9 text-center shadow-[0_1px_2px_rgba(15,23,42,0.04),0_18px_40px_rgba(15,23,42,0.08)] dark:border-slate-700 dark:bg-slate-900 dark:shadow-[0_18px_40px_rgba(0,0,0,0.35)]">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-500 to-orange-500" />

          <div
            aria-hidden
            className="relative mx-auto mb-5 inline-flex h-[84px] w-[84px] items-center justify-center rounded-full bg-gradient-to-b from-amber-50 to-amber-100 text-amber-600 shadow-[0_8px_20px_rgba(245,158,11,0.18)] dark:from-amber-950 dark:to-amber-900 dark:text-amber-300 dark:shadow-black/25"
          >
            <span className="absolute -inset-2 animate-pulse rounded-full border border-amber-400/30 dark:border-amber-300/30" />
            <CancelOutlinedIcon sx={{ fontSize: 40 }} />
          </div>

          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
            PayHere checkout
          </p>
          <h1 className="mb-2.5 text-[26px] font-bold leading-tight tracking-tight text-slate-900 dark:text-slate-50">
            Payment cancelled
          </h1>
          <p className="mx-auto mb-6 max-w-[34ch] text-sm leading-relaxed text-slate-500 dark:text-slate-400">
            You closed the checkout before completing payment. No amount was
            charged, and your subscription was not updated.
          </p>

          <div className="flex flex-col gap-2.5 sm:flex-row">
            <button
              type="button"
              onClick={() => navigate("/client/pay")}
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-[10px] border border-primary bg-gradient-to-b from-primaryLight to-primary px-4 py-2.5 text-sm font-semibold text-white shadow-[0_8px_18px_rgba(23,77,122,0.28)] transition-colors hover:from-[#2169a3] hover:to-[#1a5f95] active:translate-y-px"
            >
              <ReplayIcon sx={{ fontSize: 18 }} />
              Try again
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-[10px] border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50 active:translate-y-px dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-slate-500 dark:hover:bg-slate-700"
            >
              <HomeOutlinedIcon sx={{ fontSize: 18 }} />
              Go to dashboard
            </button>
          </div>

          <div className="mt-[22px] border-t border-slate-100 pt-4 dark:border-slate-800">
            <div
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              className="h-[3px] overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"
            >
              <span
                className="block h-full rounded-full bg-gradient-to-r from-amber-500 to-primary transition-[width] duration-1000 ease-linear"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="mt-2.5 text-xs text-slate-400">
              Redirecting to dashboard in{" "}
              <strong className="font-bold text-slate-600 dark:text-slate-300">
                {secondsLeft}s
              </strong>
            </p>
          </div>
        </article>
      </div>
    </AccountPageScroll>
  );
}

export default PayhereCancel;
